import React, { useState, useRef, useCallback } from 'react'
import styled from 'styled-components'
import BorderlessTextAreaOnly from 'components/Common/Form/Input/BorderlessTextAreaOnly'
import IconButton from 'LuxKit/Button/IconButton'
import LineTimesIcon from 'LuxKit/Icons/line/LineTimesIcon'
import LineArrowUpIcon from 'LuxKit/Icons/line/LineArrowUpIcon'
import Group from 'components/utils/Group'
import { COMMENT_MAX_LENGTH } from 'constants/comments'
import CharacterLimitWarning from './CharacterLimitWarning'

const CancelButton = styled(IconButton)`
  --button-border-color: ${props => props.theme.palette.neutral.default.five};
`

const TextAreaWithCounterWrapper = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  min-width: 0;
`

interface Props {
  onSubmit: (text: string) => void;
  onCancel: () => void;
  placeholder?: string;
  initialValue?: string;
  disabled?: boolean;
}

export default function CommentInput({
  onSubmit,
  onCancel,
  placeholder = 'Add a comment...',
  initialValue = '',
  disabled = false
}: Props) {
  const [text, setText] = useState(initialValue)
  const inputRef = useRef<HTMLTextAreaElement>(null)

  const handleChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setText(e.target.value)
  }, [])

  const handleSubmit = useCallback(() => {
    const trimmedText = text.trim()
    if (trimmedText.length > 0) {
      onSubmit(trimmedText)
      setText('')
    }
  }, [text, onSubmit])

  const handleCancel = useCallback(() => {
    setText('')
    onCancel()
    inputRef.current?.blur()
  }, [onCancel])

  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSubmit()
    } else if (e.key === 'Escape') {
      handleCancel()
    }
  }, [handleSubmit, handleCancel])

  const characterCount = text.length
  const isOverLimit = characterCount >= COMMENT_MAX_LENGTH
  const showCharacterCount = isOverLimit

  return (
    <Group direction="vertical">
      <Group direction="horizontal" gap={8} verticalAlign="center">
        <TextAreaWithCounterWrapper>
          <BorderlessTextAreaOnly
            ref={inputRef}
            value={text}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            disabled={disabled}
            maxLength={COMMENT_MAX_LENGTH}
          />
          {showCharacterCount && (
            <CharacterLimitWarning characterCount={characterCount} maxLength={COMMENT_MAX_LENGTH} />
          )}
        </TextAreaWithCounterWrapper>
        <Group direction="horizontal" gap={8} verticalAlign="center">
          <CancelButton
            kind="tertiary"
            shape="circle"
            size="small"
            onClick={handleCancel}
            disabled={disabled || text.length === 0}
            aria-label="Cancel"
          >
            <LineTimesIcon size="S" />
          </CancelButton>
          <IconButton
            kind="primary"
            shape="circle"
            size="small"
            onClick={handleSubmit}
            disabled={disabled || text.trim().length === 0}
            aria-label="Send comment"
          >
            <LineArrowUpIcon size="S" />
          </IconButton>
        </Group>
      </Group>
    </Group>
  )
}
